import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useI18n, formatDate, formatDateTime, formatMoney } from "../i18n";
import { PageHeader, Card, Badge } from "../components/shared";
import { ArrowLeft, Stethoscope, CalendarDays, FileText } from "lucide-react";

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState({ consultations: [], appointments: [], invoices: [] });

  useEffect(() => {
    (async () => {
      const [p, h] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/history`),
      ]);
      setPatient(p.data);
      setHistory(h.data);
    })();
  }, [id]);

  if (!patient) return <div>{t("common.loading")}</div>;

  return (
    <div>
      <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-2 text-ink-muted hover:text-ink mb-4 font-semibold">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <PageHeader
        title={`${patient.first_name} ${patient.last_name}`}
        subtitle={[patient.phone, patient.gender, patient.date_of_birth && formatDate(patient.date_of_birth)].filter(Boolean).join(" · ")}
        testid="patient-detail-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="font-heading font-bold text-ink mb-4">{t("common.details")}</h3>
          <dl className="space-y-2 text-sm">
            <Row k={t("patients.bloodGroup")} v={patient.blood_group || "-"} />
            <Row k={t("patients.allergies")} v={patient.allergies?.length ? patient.allergies.join(", ") : "-"} />
            <Row k={t("patients.insurance")} v={patient.insurance || "-"} />
            <Row k={t("patients.emergency")} v={patient.emergency_contact || "-"} />
            <Row k={t("patients.address")} v={patient.address || "-"} />
          </dl>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading font-bold text-ink mb-4 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-brand" /> {t("consultations.title")}
          </h3>
          {history.consultations.length === 0 ? (
            <div className="text-ink-muted text-sm">{t("common.noData")}</div>
          ) : (
            <ul className="divide-y divide-[var(--border-default)]">
              {history.consultations.map((c) => (
                <li key={c.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-ink">{c.diagnosis || "-"}</div>
                    <div className="text-xs text-ink-muted">{formatDateTime(c.created_at)}</div>
                  </div>
                  {c.notes && <div className="text-sm text-ink-muted mt-1">{c.notes}</div>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading font-bold text-ink mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-brand" /> {t("appointments.title")}
          </h3>
          {history.appointments.length === 0 ? <div className="text-ink-muted text-sm">{t("common.noData")}</div> : (
            <ul className="divide-y divide-[var(--border-default)]">
              {history.appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-ink">{formatDateTime(a.start_at)}</div>
                    <div className="text-xs text-ink-muted">{a.reason || "-"}</div>
                  </div>
                  <Badge tone={a.status === "completed" ? "success" : a.status === "cancelled" ? "danger" : "info"}>
                    {t(`appointments.status.${a.status}`)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-bold text-ink mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand" /> {t("invoices.title")}
          </h3>
          {history.invoices.length === 0 ? <div className="text-ink-muted text-sm">{t("common.noData")}</div> : (
            <ul className="divide-y divide-[var(--border-default)]">
              {history.invoices.map((i) => (
                <li key={i.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs text-ink-muted">{i.invoice_number}</div>
                    <Badge tone={i.status === "paid" ? "success" : i.status === "partial" ? "warning" : "danger"}>
                      {t(`invoices.status.${i.status}`)}
                    </Badge>
                  </div>
                  <div className="stat-number font-bold text-ink">{formatMoney(i.total)}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-muted">{k}</dt>
      <dd className="font-semibold text-ink text-right">{v}</dd>
    </div>
  );
}
